import type { CSSProperties, ReactNode } from 'react'

import { useRef } from 'react'

import { cn } from '@/lib/utils'
import {
    gsap,
    registerGsap,
    wfEase,
    prefersReducedMotion
} from '@/lib/gsap'
import { useIsomorphicLayoutEffect } from '@/hooks/use-isomorphic-layout-effect'

type RevealMode = 'load' | 'view'

interface BaseProps {
    children?: ReactNode
    mode?: RevealMode
    delay?: number
    duration?: number
    className?: string
    style?: CSSProperties
}

const VIEW_START = 'top 88%'

// Perspective text reveal: rotateX(-100deg) + translateY(60%) + opacity 0 -> resting.
// This is the signature Webflow "perspective-load" entrance.
interface RevealProps extends BaseProps {
    large?: boolean
    innerClassName?: string
    // when false, render only the animated element (to nest inside an existing .perspective)
    wrapper?: boolean
}

export function Reveal({
    children,
    mode = 'view',
    delay = 0,
    duration = 0.8,
    large,
    className,
    innerClassName,
    wrapper = true
}: RevealProps) {
    const ref = useRef<HTMLDivElement>(null)

    useIsomorphicLayoutEffect(() => {
        const el = ref.current
        if (!el) return

        registerGsap()

        if (prefersReducedMotion()) {
            gsap.set(el, { opacity: 1, clearProps: 'transform' })
            return
        }

        const ctx = gsap.context(() => {
            const from = {
                rotationX: -100,
                y: '60%',
                z: () => -window.innerWidth * 0.03,
                opacity: 0,
                transformOrigin: '50% 100%'
            }
            const to = {
                rotationX: 0,
                y: '0%',
                z: 0,
                opacity: 1,
                duration,
                delay,
                ease: wfEase('ease'),
                ...(mode === 'view'
                    ? { scrollTrigger: { trigger: el, start: VIEW_START, once: true } }
                    : {})
            }
            gsap.fromTo(el, from, to)
        }, ref)

        return () => ctx.revert()
    }, [mode, delay, duration])

    const inner = (
        <div ref={ref} className={innerClassName} style={{ opacity: 0 }}>
            {children}
        </div>
    )

    if (!wrapper) return inner

    return (
        <div className={cn('perspective', large && 'large-perspective', className)}>
            {inner}
        </div>
    )
}

// Slow opacity fade (Webflow "fade-in-slow").
export function FadeIn({
    children,
    mode = 'view',
    delay = 0,
    duration = 1.2,
    className,
    style
}: BaseProps) {
    const ref = useRef<HTMLDivElement>(null)

    useIsomorphicLayoutEffect(() => {
        const el = ref.current
        if (!el) return

        registerGsap()

        if (prefersReducedMotion()) {
            gsap.set(el, { opacity: 1 })
            return
        }

        const ctx = gsap.context(() => {
            gsap.fromTo(
                el,
                { opacity: 0 },
                {
                    opacity: 1,
                    duration,
                    delay,
                    ease: wfEase('ease'),
                    ...(mode === 'view'
                        ? { scrollTrigger: { trigger: el, start: VIEW_START, once: true } }
                        : {})
                }
            )
        }, ref)

        return () => ctx.revert()
    }, [mode, delay, duration])

    return (
        <div ref={ref} className={className} style={{ opacity: 0, ...style }}>
            {children}
        </div>
    )
}

// Opacity + small upward translate (Webflow "translate 20px / opacity 0" entrance).
interface FadeUpProps extends BaseProps {
    distance?: number
}

export function FadeUp({
    children,
    mode = 'view',
    delay = 0,
    duration = 0.8,
    distance = 20,
    className,
    style
}: FadeUpProps) {
    const ref = useRef<HTMLDivElement>(null)

    useIsomorphicLayoutEffect(() => {
        const el = ref.current
        if (!el) return

        registerGsap()

        if (prefersReducedMotion()) {
            gsap.set(el, { opacity: 1, y: 0 })
            return
        }

        const ctx = gsap.context(() => {
            gsap.fromTo(
                el,
                { opacity: 0, y: distance },
                {
                    opacity: 1,
                    y: 0,
                    duration,
                    delay,
                    ease: wfEase('ease'),
                    ...(mode === 'view'
                        ? { scrollTrigger: { trigger: el, start: VIEW_START, once: true } }
                        : {})
                }
            )
        }, ref)

        return () => ctx.revert()
    }, [mode, delay, duration, distance])

    return (
        <div ref={ref} className={className} style={{ opacity: 0, ...style }}>
            {children}
        </div>
    )
}
